<script setup lang="ts">
import { computed } from "vue";

import type { ChatTurn } from "@/composables/useChat.js";
import { renderMarkdown } from "@/lib/markdown.js";

import ConfidenceChip from "./ConfidenceChip.vue";
import ToolTrail from "./ToolTrail.vue";

const props = defineProps<{ turn: ChatTurn }>();

const html = computed(() => renderMarkdown(props.turn.content));
const needsReview = computed(() => props.turn.grounding?.needsReview ?? false);
</script>

<template>
  <article v-if="turn.role === 'user'" class="turn user">
    <div class="bubble">
      {{ turn.content }}
      <ul v-if="turn.attachmentNames?.length" class="files">
        <li v-for="name in turn.attachmentNames" :key="name">{{ name }}</li>
      </ul>
    </div>
  </article>

  <article v-else class="turn assistant card">
    <header>
      <span class="who">Assistendi vastus</span>
      <ConfidenceChip v-if="turn.grounding" :grounding="turn.grounding" />
    </header>

    <!-- Same rule as Phase 1: an unsure answer is flagged and queued, never
         presented as fact. -->
    <p v-if="needsReview" class="review">
      Vajab inimese ülevaatust — lisasin küsimuse vastamata küsimuste nimekirja.
      <span v-if="turn.grounding?.note" class="note">{{ turn.grounding.note }}</span>
    </p>

    <!-- eslint-disable-next-line vue/no-v-html -- markdown is escaped in renderMarkdown -->
    <div v-if="turn.content" class="body" v-html="html" />
    <p v-else-if="turn.streaming" class="thinking">Mõtlen…</p>

    <p v-if="turn.error" class="error">{{ turn.error }}</p>

    <ToolTrail :calls="turn.toolCalls" />

    <footer v-if="turn.usage">
      <span class="pill">sisend {{ turn.usage.inputTokens }} tokenit</span>
      <span class="pill">väljund {{ turn.usage.outputTokens }} tokenit</span>
    </footer>
  </article>
</template>

<style scoped>
.files {
  list-style: none;
  margin: 0.4rem 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.files li {
  font-size: 0.72rem;
  opacity: 0.85;
  border: 1px solid currentcolor;
  border-radius: 999px;
  padding: 0.05rem 0.45rem;
}

.turn {
  margin-bottom: 1rem;
}

.user {
  display: flex;
  justify-content: flex-end;
}

.user .bubble {
  background: var(--soft);
  border: 1px solid var(--line);
  border-radius: var(--radius) var(--radius) 2px var(--radius);
  padding: 0.6rem 0.9rem;
  max-width: 42rem;
  white-space: pre-wrap;
}

.assistant {
  padding: 1rem 1.15rem;
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
}

.who {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}

.review {
  margin: 0 0 0.7rem;
  padding: 0.5rem 0.7rem;
  border-left: 3px solid var(--gold);
  background: var(--soft);
  border-radius: 0 var(--radius) var(--radius) 0;
  font-size: 0.85rem;
  color: var(--steel);
}

.note {
  display: block;
  color: var(--muted);
  margin-top: 0.2rem;
}

.body :deep(p) {
  margin: 0 0 0.6rem;
}

.body :deep(p:last-child) {
  margin-bottom: 0;
}

.body :deep(ol),
.body :deep(ul) {
  margin: 0 0 0.6rem;
  padding-left: 1.3rem;
}

.body :deep(li) {
  margin-bottom: 0.25rem;
}

.body :deep(code) {
  background: var(--soft);
  border: 1px solid var(--line);
  border-radius: 4px;
  padding: 0.05rem 0.3rem;
  font-size: 0.88em;
}

.body :deep(pre) {
  background: var(--soft);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 0.7rem 0.85rem;
  overflow-x: auto;
}

.body :deep(pre code) {
  border: none;
  background: none;
  padding: 0;
}

.body :deep(a) {
  color: var(--green-dark);
}

.thinking {
  margin: 0;
  color: var(--muted);
  font-style: italic;
}

.error {
  margin: 0.6rem 0 0;
  color: var(--warn);
  font-size: 0.88rem;
}

footer {
  margin-top: 0.75rem;
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}
</style>
